import React from 'react';
import { Link } from 'react-router-dom';

const PolitiqueRGPDSante = () => {
  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Bouton retour */}
      <Link
        to="/sejours"
        className="inline-flex items-center text-primary-600 hover:text-primary-700 mb-6"
      >
        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
        Retour
      </Link>

      {/* En-tête */}
      <div className="bg-white shadow rounded-lg p-8 mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">
          Protection des données de santé de votre enfant
        </h1>
        <p className="text-lg text-gray-600">
          Informations importantes sur le traitement des données sensibles conformément au RGPD
        </p>
      </div>

      {/* Section 1: Pourquoi collectons-nous ces données ? */}
      <div className="bg-white shadow rounded-lg p-8 mb-6">
        <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
          <svg className="w-8 h-8 mr-3 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Pourquoi collectons-nous ces données ?
        </h2>
        <div className="space-y-4 text-gray-700">
          <p>
            Les données de santé de votre enfant sont des <strong>informations sensibles</strong> protégées
            par l'<strong>article 9 du Règlement Général sur la Protection des Données (RGPD)</strong>.
          </p>
          <p>
            Nous collectons ces informations pour assurer la <strong>sécurité et le bien-être</strong> de votre
            enfant pendant le séjour. Ces données nous permettent de :
          </p>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li>Adapter les repas en fonction du régime alimentaire (allergies, intolérances, convictions)</li>
            <li>Être informés des conditions médicales particulières</li>
            <li>Contacter rapidement le médecin traitant en cas d'urgence</li>
            <li>Prendre toutes les dispositions nécessaires pour la santé de votre enfant</li>
          </ul>
        </div>
      </div>

      {/* Section 2: Base légale du traitement */}
      <div className="bg-white shadow rounded-lg p-8 mb-6">
        <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
          <svg className="w-8 h-8 mr-3 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
          Base légale du traitement
        </h2>
        <div className="space-y-4 text-gray-700">
          <p>
            Le traitement de ces données de santé repose sur <strong>deux bases légales</strong> :
          </p>
          <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
            <h3 className="font-semibold text-blue-900 mb-2">1. Exécution du contrat (Art. 6.1.b RGPD)</h3>
            <p className="text-blue-800">
              Ces informations sont <strong>nécessaires à l'exécution du contrat d'inscription</strong> au séjour.
              Sans ces données, nous ne pouvons pas garantir la sécurité et le bien-être de votre enfant,
              et nous ne serons donc pas en mesure de traiter l'inscription.
            </p>
          </div>
          <div className="bg-green-50 border-l-4 border-green-400 p-4 rounded">
            <h3 className="font-semibold text-green-900 mb-2">2. Consentement explicite (Art. 9.2.a RGPD)</h3>
            <p className="text-green-800">
              Pour les données de santé sensibles, nous demandons votre <strong>consentement explicite</strong>
              conformément à l'article 9 du RGPD qui protège spécifiquement ces catégories de données.
            </p>
          </div>
        </div>
      </div>

      {/* Section 3: Quelles données collectons-nous ? */}
      <div className="bg-white shadow rounded-lg p-8 mb-6">
        <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
          <svg className="w-8 h-8 mr-3 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
          </svg>
          Quelles données de santé collectons-nous ?
        </h2>
        <div className="space-y-3 text-gray-700">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-green-500 mr-3 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <div>
              <strong>Coordonnées du médecin traitant</strong> (nom, prénom, téléphone)
            </div>
          </div>
          <div className="flex items-start">
            <svg className="w-5 h-5 text-green-500 mr-3 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <div>
              <strong>Régime alimentaire spécifique</strong> (allergies, intolérances, convictions religieuses ou philosophiques)
            </div>
          </div>
          <div className="flex items-start">
            <svg className="w-5 h-5 text-green-500 mr-3 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <div>
              <strong>Remarques complémentaires</strong> sur l'état de santé (traitements en cours, allergies, handicaps, etc.)
            </div>
          </div>
          <div className="flex items-start">
            <svg className="w-5 h-5 text-green-500 mr-3 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <div>
              <strong>Documents sanitaires obligatoires</strong> (fiche sanitaire, certificat de vaccination, etc.)
            </div>
          </div>
        </div>
      </div>

      {/* Section 4: Qui a accès à ces données ? */}
      <div className="bg-white shadow rounded-lg p-8 mb-6">
        <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
          <svg className="w-8 h-8 mr-3 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
          Qui a accès à ces données ?
        </h2>
        <div className="space-y-4 text-gray-700">
          <p>
            L'accès aux données de santé est <strong>strictement limité</strong> aux personnes habilitées :
          </p>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li><strong>Les administrateurs du centre</strong> (pour organiser les séjours en toute sécurité)</li>
            <li><strong>L'équipe d'encadrement</strong> du séjour (animateurs, directeurs)</li>
            <li><strong>Le personnel médical</strong> en cas de besoin</li>
            <li><strong>Les services d'urgence</strong> si nécessaire</li>
          </ul>
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded mt-4">
            <p className="text-yellow-800">
              <strong>Confidentialité garantie :</strong> Toutes les personnes ayant accès à ces données
              sont tenues au secret professionnel et ont signé des engagements de confidentialité.
            </p>
          </div>
        </div>
      </div>

      {/* Section 5: Combien de temps conservons-nous ces données ? */}
      <div className="bg-white shadow rounded-lg p-8 mb-6">
        <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
          <svg className="w-8 h-8 mr-3 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Combien de temps conservons-nous ces données ?
        </h2>
        <div className="space-y-4 text-gray-700">
          <p>
            Les données de santé sont conservées selon les durées suivantes :
          </p>
          <div className="bg-gray-50 p-4 rounded border border-gray-200">
            <div className="flex justify-between items-center mb-2">
              <span><strong>Pendant le séjour :</strong></span>
              <span className="text-primary-600 font-semibold">Conservation active</span>
            </div>
            <p className="text-sm text-gray-600">Données accessibles à l'équipe d'encadrement</p>
          </div>
          <div className="bg-gray-50 p-4 rounded border border-gray-200">
            <div className="flex justify-between items-center mb-2">
              <span><strong>Après le séjour :</strong></span>
              <span className="text-primary-600 font-semibold">Archivage sécurisé (1 an)</span>
            </div>
            <p className="text-sm text-gray-600">
              Conservation pour des raisons légales (responsabilité civile) puis suppression automatique
            </p>
          </div>
          <div className="bg-gray-50 p-4 rounded border border-gray-200">
            <div className="flex justify-between items-center mb-2">
              <span><strong>Données financières :</strong></span>
              <span className="text-primary-600 font-semibold">10 ans (obligation légale)</span>
            </div>
            <p className="text-sm text-gray-600">
              Seules les données financières sont conservées conformément aux obligations comptables
            </p>
          </div>
        </div>
      </div>

      {/* Section 6: Vos droits */}
      <div className="bg-white shadow rounded-lg p-8 mb-6">
        <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
          <svg className="w-8 h-8 mr-3 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Vos droits sur ces données
        </h2>
        <div className="space-y-4 text-gray-700">
          <p>Conformément au RGPD, vous disposez des droits suivants :</p>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-blue-50 p-4 rounded">
              <h3 className="font-semibold text-blue-900 mb-2">Droit d'accès</h3>
              <p className="text-sm text-blue-800">Consulter les données que nous détenons sur votre enfant</p>
            </div>
            <div className="bg-blue-50 p-4 rounded">
              <h3 className="font-semibold text-blue-900 mb-2">Droit de rectification</h3>
              <p className="text-sm text-blue-800">Corriger des données inexactes ou incomplètes</p>
            </div>
            <div className="bg-blue-50 p-4 rounded">
              <h3 className="font-semibold text-blue-900 mb-2">Droit d'opposition</h3>
              <p className="text-sm text-blue-800">Refuser le traitement de certaines données (sauf obligations légales)</p>
            </div>
            <div className="bg-blue-50 p-4 rounded">
              <h3 className="font-semibold text-blue-900 mb-2">Droit à la limitation</h3>
              <p className="text-sm text-blue-800">Limiter temporairement l'utilisation de vos données</p>
            </div>
            <div className="bg-blue-50 p-4 rounded">
              <h3 className="font-semibold text-blue-900 mb-2">Droit à la portabilité</h3>
              <p className="text-sm text-blue-800">Récupérer vos données dans un format structuré</p>
            </div>
            <div className="bg-blue-50 p-4 rounded">
              <h3 className="font-semibold text-blue-900 mb-2">Droit à l'oubli</h3>
              <p className="text-sm text-blue-800">Demander la suppression de vos données (sous conditions)</p>
            </div>
          </div>
          <div className="bg-green-50 border-l-4 border-green-400 p-4 rounded mt-6">
            <p className="text-green-800">
              <strong>Comment exercer vos droits ?</strong> Contactez-nous à tout moment par email ou courrier.
              Nous vous répondrons dans un délai maximum d'un mois.
            </p>
          </div>
        </div>
      </div>

      {/* Section 7: Que se passe-t-il si je refuse le consentement ? */}
      <div className="bg-red-50 border-l-4 border-red-400 shadow rounded-lg p-8 mb-6">
        <h2 className="text-2xl font-semibold text-red-900 mb-4 flex items-center">
          <svg className="w-8 h-8 mr-3 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          Que se passe-t-il si je refuse le consentement ?
        </h2>
        <div className="space-y-4 text-red-800">
          <p className="font-semibold">
            Le consentement pour le traitement des données de santé est <strong>nécessaire à l'exécution
            du contrat d'inscription</strong> au séjour.
          </p>
          <p>
            <strong>En cas de refus, nous ne serons malheureusement pas en mesure de traiter l'inscription</strong>
            de votre enfant, car nous ne pourrons pas garantir sa sécurité et son bien-être pendant le séjour.
          </p>
          <div className="bg-white p-4 rounded border border-red-200 mt-4">
            <p className="text-gray-800">
              <strong>Pourquoi ?</strong> Sans ces informations, nous ne pourrions pas :
            </p>
            <ul className="list-disc list-inside space-y-1 ml-4 mt-2 text-gray-700">
              <li>Adapter les repas en cas d'allergies alimentaires (risque vital)</li>
              <li>Contacter le médecin traitant en cas d'urgence</li>
              <li>Administrer les traitements médicaux nécessaires</li>
              <li>Prendre les précautions adaptées à l'état de santé de votre enfant</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Section 8: Sécurité des données */}
      <div className="bg-white shadow rounded-lg p-8 mb-6">
        <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
          <svg className="w-8 h-8 mr-3 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
          Sécurité et protection de vos données
        </h2>
        <div className="space-y-4 text-gray-700">
          <p>
            Nous mettons en œuvre toutes les mesures techniques et organisationnelles appropriées
            pour protéger vos données :
          </p>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li><strong>Chiffrement</strong> des données sensibles dans notre base de données</li>
            <li><strong>Accès sécurisé</strong> avec authentification forte</li>
            <li><strong>Traçabilité</strong> de tous les accès aux données médicales</li>
            <li><strong>Sauvegardes régulières</strong> et sécurisées</li>
            <li><strong>Serveurs hébergés en France</strong> conformément au RGPD</li>
            <li><strong>Formation du personnel</strong> à la protection des données</li>
          </ul>
        </div>
      </div>

      {/* Contact et réclamation */}
      <div className="bg-white shadow rounded-lg p-8">
        <h2 className="text-2xl font-semibold text-gray-900 mb-4">
          Questions ou réclamation ?
        </h2>
        <div className="space-y-4 text-gray-700">
          <p>
            Si vous avez des questions concernant le traitement de vos données ou si vous souhaitez
            exercer vos droits, vous pouvez nous contacter à tout moment.
          </p>
          <p>
            Si vous estimez que vos droits ne sont pas respectés, vous avez également le droit
            d'introduire une réclamation auprès de la <strong>Commission Nationale de l'Informatique
            et des Libertés (CNIL)</strong>.
          </p>
          <div className="bg-gray-50 p-4 rounded border border-gray-200">
            <p className="text-sm">
              <strong>CNIL - 3 Place de Fontenoy</strong><br />
              TSA 80715 - 75334 PARIS CEDEX 07<br />
              Tél. : 01 53 73 22 22<br />
              Web : <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">www.cnil.fr</a>
            </p>
          </div>
        </div>
      </div>

      {/* Bouton retour en bas */}
      <div className="mt-8 text-center">
        <Link
          to="/sejours"
          className="inline-flex items-center bg-primary-600 text-white px-6 py-3 rounded-md hover:bg-primary-700 font-medium"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Retour aux séjours
        </Link>
      </div>
    </div>
  );
};

export default PolitiqueRGPDSante;
